import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { Controller, type DefaultValues, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { validate as isUuid } from 'uuid';
import { z } from 'zod';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { BROKERS_LIST_QUERY_KEY } from '@/hooks/queries/brokers/use-brokers';
import { getErrorMessage } from '@/lib/api/error';
import { buildCreateBrokerRequest, createBroker } from '@/services/api/brokers';
import { BROKER_SETTING_OPTIONS, BROKER_SETTING_VALUES, BROKER_TYPE_LABELS, BROKER_TYPE_VALUES } from './broker-options';

// Client-side only — stricter than the API's min-length-1: lowercase
// letters and single hyphens, no leading/trailing/double hyphen, no digits.
const BROKER_NAME_PATTERN = /^[a-z]+(?:-[a-z]+)*$/;

const newBrokerFormSchema = z.object({
  accountHolderId: z
    .string()
    .min(1, 'Account Holder ID is required')
    .refine((value) => isUuid(value), 'Account Holder ID must be a valid UUID'),
  brokerName: z
    .string()
    .min(1, 'Broker Name is required')
    .min(2, 'Broker Name must be at least 2 characters')
    .regex(BROKER_NAME_PATTERN, 'Broker Name must contain only lowercase letters and hyphens'),
  brokerType: z.enum(BROKER_TYPE_VALUES, { error: 'Broker Type is required' }),
  brokerSettings: z.array(z.enum(BROKER_SETTING_VALUES)),
});

type NewBrokerFormSchema = z.infer<typeof newBrokerFormSchema>;

// brokerType is deliberately absent — the required select starts empty, blocking Create
const FORM_DEFAULTS: DefaultValues<NewBrokerFormSchema> = {
  accountHolderId: '',
  brokerName: '',
  brokerSettings: [],
};

interface NewBrokerDialogProps {
  /** Unmount-on-close resets the whole form, errors and checkboxes included */
  onClose: () => void;
}

export const NewBrokerDialog = ({ onClose }: NewBrokerDialogProps) => {
  const queryClient = useQueryClient();

  const form = useForm<NewBrokerFormSchema>({
    resolver: zodResolver(newBrokerFormSchema),
    mode: 'onChange',
    defaultValues: FORM_DEFAULTS,
  });

  // Create re-validates via handleSubmit before the audit confirm opens
  const handleCreate = form.handleSubmit(async (values) => {
    const justification = await confirmAction({
      title: 'Confirm Add new broker',
      description: `Proceeding with this action will create a new broker ${values.brokerName} for account holder ${values.accountHolderId}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Add new broker',
      onConfirm: async (comment) => {
        try {
          // comment is the required audit justification — mandatory client-side even
          // though the API marks it optional. Empty brokerSettings must OMIT
          // the broker_settings key entirely — buildCreateBrokerRequest owns
          // that rule. The 201 body is opaque and ignored; the list is refetched instead.
          await createBroker(
            buildCreateBrokerRequest({
              accountHolderId: values.accountHolderId,
              brokerName: values.brokerName,
              brokerType: values.brokerType,
              brokerSettings: values.brokerSettings,
              comment,
            }),
          );
        } catch (error) {
          // Legacy flattened every failure to a generic 500, hiding the real Zod code
          // — surface the upstream error; the rethrow keeps the audit
          // dialog open and recoverable.
          throw new Error(`Failed to create broker: ${getErrorMessage(error)}`);
        }
      },
    });
    if (justification === null) return; // cancelled — form dialog stays open
    toast.success(`Successfully created broker ${values.brokerName}`);
    await queryClient.invalidateQueries({ queryKey: BROKERS_LIST_QUERY_KEY });
    onClose();
  });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            New Broker
          </DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" noValidate onSubmit={handleCreate}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-broker-account-holder-id">Account Holder ID</Label>
            <Input id="new-broker-account-holder-id" {...form.register('accountHolderId')} />
            <FieldError message={form.formState.errors.accountHolderId?.message} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-broker-name">Broker Name</Label>
            <Input id="new-broker-name" placeholder="e.g. acme-broker" {...form.register('brokerName')} />
            <FieldError message={form.formState.errors.brokerName?.message} />
          </div>

          <Controller
            control={form.control}
            name="brokerType"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new-broker-type">Broker Type</Label>
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <SelectTrigger id="new-broker-type">
                    <SelectValue placeholder="Select broker type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BROKER_TYPE_VALUES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {BROKER_TYPE_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />

          <Separator />

          <h3 className="text-sm font-semibold">Broker Settings</h3>
          <Controller
            control={form.control}
            name="brokerSettings"
            render={({ field }) => {
              const selected: readonly string[] = field.value;
              return (
                <div className="flex flex-col gap-3">
                  {BROKER_SETTING_OPTIONS.map((option) => {
                    const checkboxId = `new-broker-setting-${option.value}`;
                    return (
                      <label className="flex cursor-pointer items-start gap-3" htmlFor={checkboxId} key={option.value}>
                        <Checkbox
                          checked={selected.includes(option.value)}
                          className="mt-0.5"
                          id={checkboxId}
                          onCheckedChange={(checked) => field.onChange(checked === true ? [...selected, option.value] : selected.filter((value) => value !== option.value))}
                        />
                        <span className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium">{option.title}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              );
            }}
          />

          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!form.formState.isValid} type="submit" variant="primary">
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
NewBrokerDialog.displayName = 'NewBrokerDialog';
